import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Row, Col, ListGroup, Button } from "react-bootstrap";

import DatePicker from "react-datepicker";

import utils from "../../config/utils";
import DataSource from "../../config/api";
import ConstUtils from "../../config/constants";
import DefaultLoader from "./DefaultLoader";

import "../../style/appointmentForm.css";
import "react-datepicker/dist/react-datepicker.css";
import PolicyDetails from "./PolicyDetails";

const AppointmentForm = (props) => {
    const {
        id,
        cityObj,
        stateObj,
        wellnessId,
        isCenterVisit,
        isPackageMapped,
        policyBuyerName,
        isFastingRequired,
        insuranceCompanyName,
    } = props.caseDetails;

    const history = useHistory();

    const [formData, setFormData] = useState({
        pincode: props.userPincode || "",
        city: cityObj?.name || "",
        state: stateObj?.name || "",
        visitType: "",
        diagnosticCenter: "",
        preferredDate: "",
        preferredTime: "",
        address: "",
        landmark: "",
    });

    const [pincodeDetails, setPincodeDetails] = useState({
        cityId: cityObj?.id || "",
        cityName: cityObj?.name || "",
        stateId: stateObj?.id || "",
        stateName: stateObj?.name || "",
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isValidPincode, setIsValidPincode] = useState(true);
    const [pincodeErrMessage, setPincodeErrMessage] = useState("");

    const [bookingError, setBookingError] = useState("");
    const [visitTypeError, setVisitTypeError] = useState("");
    const [isHomeVisitMapped, setIsHomeVisitMapped] = useState(true);

    const [dcSearchValue, setDCSearchValue] = useState("");
    const [dcSearchError, setDCSearchError] = useState("");
    const [dcDropdownVisible, setDcDropDownVisible] = useState(false);
    const [diagnosticCenters, setDiagnosticCenters] = useState([]);
    const [filteredDiagnosticCenters, setFilteredDiagnosticCenters] = useState([]);

    const [dateErrMessage, setDateErrMessage] = useState("");
    const [slotDropDownVisible, setSlotDropDownVisible] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsErrMessage, setSlotsErrMessage] = useState("");

    useEffect(() => {
        async function fetchPincodeData() {
            if (props.userPincode) {
                await fetchPincodeDetails(props.userPincode);
            }
        }
        fetchPincodeData();
    }, [props.userPincode]);

    const handleInputChange = async (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        switch (field) {
            case "pincode":
                if (!value.match(/^[0-9]+$/) || value.length > 6 || value.length < 6) {
                    setIsValidPincode(false);
                    setPincodeErrMessage("Enter a valid pincode");
                } else if (value.length === 6 && !isNaN(value)) {
                    resetForm();
                    resetFormErrors();
                    setIsValidPincode(true);
                    await fetchPincodeDetails(value);
                }
                break;

            case "visitType":
                handleVisitTypeChange();
                break;

            default:
                break;
        }
    };

    const handleVisitTypeChange = () => {
        if (!formData.pincode || !isValidPincode) {
            setFormData((prev) => ({
                ...prev,
                visitType: "",
            }));
            setVisitTypeError(
                !isValidPincode ? "Please enter valid pincode" : "Please enter pincode"
            );
            return;
        }
        resetForm();
    };

    const handleDCSearchChange = (e) => {
        const query = e.target.value;
        setDCSearchValue(query);

        if (query.trim() === "") {
            setFilteredDiagnosticCenters(diagnosticCenters);
        } else {
            setFilteredDiagnosticCenters(
                diagnosticCenters.filter((center) =>
                    center?.entity?.name.toLowerCase().includes(query.toLowerCase())
                )
            );
        }
        setDcDropDownVisible(true);
    };

    const selectDiagnosticCenter = async (center, index) => {
        setDCSearchValue(center?.entity?.name);
        setFormData((prev) => ({
            ...prev,
            diagnosticCenter: diagnosticCenters[index],
            preferredTime: "",
        }));
        setDcDropDownVisible(false);
        await fetchSlots(diagnosticCenters[index]);
    };

    const handleDateChange = async (date) => {
        if (!isValidPincode || !formData.pincode) {
            setDateErrMessage("Please enter valid pincode");
            return;
        }

        if (!formData.visitType) {
            setDateErrMessage("Please select visit type");
            return;
        }

        resetForm();
        setFormData((prev) => ({
            ...prev,
            preferredDate: date,
        }));

        isPackageMapped
            ? await fetchDaignosticCenterDetails(
                formData.pincode,
                formData.visitType === ConstUtils.visitTypeEnum.DcVisist,
                date
            )
            : await fetchSlots();
    };

    const handleSlotChange = () => {
        setSlotDropDownVisible(true);
    };

    const selectSlot = (time) => {
        setFormData((prev) => ({
            ...prev,
            preferredTime: time,
        }));
        setSlotDropDownVisible(false);
    };

    const resetForm = () => {
        setDCSearchValue("");
        setDateErrMessage("");
        setAvailableSlots([]);
        setSlotsErrMessage("");
        setFilteredDiagnosticCenters([]);

        setFormData((prev) => ({
            ...prev,
            preferredDate: "",
            preferredTime: "",
            diagnosticCenter: "",
        }));
    };

    const resetFormErrors = () => {
        setBookingError("");
        setDCSearchError("");
        setVisitTypeError("");
        setDateErrMessage("");
    };

    const isFormValid = () => {
        if (
            !formData.pincode ||
            !formData.city ||
            !formData.state ||
            !formData.visitType ||
            !formData.preferredDate ||
            !formData.preferredTime ||
            (isPackageMapped && !formData.visitType)
        ) {
            return false;
        }

        if (
            formData.visitType &&
            formData.visitType === ConstUtils.visitTypeEnum.DcVisist
        ) {
            if (!formData.diagnosticCenter) {
                return false;
            }
        }

        if (
            formData.visitType &&
            formData.visitType === ConstUtils.visitTypeEnum.HomeVisit
        ) {
            if (!formData.address) {
                return false;
            }
        }

        return true;
    };

    const fetchPincodeDetails = async (pincode) => {
        setIsLoading(true);

        const response = await DataSource.getPincodeDetails({
            pincode,
            requestId: id,
        });

        if (response?.success) {
            setPincodeDetails({
                cityId: response?.cityId,
                cityName: response?.cityName,
                stateId: response?.stateId,
                stateName: response?.stateName,
            });

            setIsValidPincode(true);
            setPincodeErrMessage("");

            setFormData({
                pincode: pincode,
                city: response.cityName || "",
                state: response.stateName || "",
                visitType: isCenterVisit
                    ? ConstUtils.visitTypeEnum.DcVisist
                    : ConstUtils.visitTypeEnum.HomeVisit,
                diagnosticCenter: "",
                preferredDate: "",
                preferredTime: "",
                address: "",
                landmark: "",
            });

            if (isCenterVisit) {
                setIsHomeVisitMapped(false);
                setVisitTypeError("Home visit is not available for your test package");
            }

            if(isPackageMapped) {
                DataSource.postActivity({
                    RequestId: id,
                    CompaignId: "",
                    TemplateName: "",
                    EQuote: wellnessId,
                    CallBackStatus: "",
                    EventType: isCenterVisit
                        ? "Package_Not_Available_For_Home_Visit"
                        : "Package_Available_For_Home_Visit",
                    EventPayload: JSON.stringify({
                        isCenterVisit,
                        isPackageMapped,
                    }),
                    meta: JSON.stringify(props.utmParams),
                    campaignId: "202010201700",
                });
            }
        } else {
            setIsValidPincode(false);
            setPincodeErrMessage(
                "Sorry, we don't serve this location. Please try a nearby Pin code or call 080-68239341"
            );
            setFormData({
                pincode: pincode,
                city: "",
                state: "",
                visitType: "",
                diagnosticCenter: "",
                preferredDate: "",
                preferredTime: "",
                address: "",
                landmark: "",
            });
        }

        setIsLoading(false);
    };

    const fetchDaignosticCenterDetails = async (
        pincode,
        isCenterVisit,
        appointmentDate
    ) => {
        setIsLoading(true);
        try {
            const requestPayload = {
                pinCode: pincode || formData.pincode,
                requestId: id,
                appointmentDate: utils.getCurrentDate(
                    appointmentDate || formData.preferredDate
                ),
            };

            const response = await DataSource.getNearestDCForBooking(
                requestPayload,
                isCenterVisit
            );

            if (!response.data.success) {
                setDCSearchError(
                    "Diagnostic Centers are not available at your location"
                );
                if (!isCenterVisit) {
                    setAvailableSlots(utils.getDefaultSlots());
                }
            } else {
                if (!isCenterVisit) {
                    let isSlotsAvailable;
                    for (const diagnosticCenter of response.data?.mappedDCList) {
                        isSlotsAvailable = await fetchSlots(
                            diagnosticCenter,
                            appointmentDate,
                            false
                        );

                        if (isSlotsAvailable) {
                            setDCSearchValue(diagnosticCenter?.entity?.name);
                            setDiagnosticCenters(diagnosticCenter);
                            setFormData((prev) => ({
                                ...prev,
                                diagnosticCenter: diagnosticCenter,
                            }));
                            break;
                        }
                    }

                    if (!isSlotsAvailable) {
                        setDCSearchValue(response.data?.mappedDCList[0]?.entity?.name);
                        setFormData((prev) => ({
                            ...prev,
                            diagnosticCenter: response.data?.mappedDCList[0],
                        }));
                        setSlotsErrMessage(
                            "No slots available, please select another date"
                        );
                    }
                } else {
                    setDCSearchValue("");
                }

                setDCSearchError("");
                setDiagnosticCenters(response.data?.mappedDCList);
                setFilteredDiagnosticCenters(response.data?.mappedDCList);
            }
        } catch (error) {
            setDCSearchError(
                "Oops something went wrong while fetching diagnostic centers"
            );
        }
        setIsLoading(false);
    };

    const fetchSlots = async (dcDetails, appointmentDate, showError = true) => {
        setIsLoading(true);
        try {
            if (isPackageMapped) {
                const response = await DataSource.getAvailableSlots({
                    HA: false,
                    providerId: dcDetails?.entity?.id,
                    appointmentDate: utils.getCurrentDate(
                        appointmentDate || formData.preferredDate
                    ),
                    homeVisit: formData.visitType === ConstUtils.visitTypeEnum.HomeVisit,
                });

                const slotsAvailable = response?.slotDetails?.slotCount?.filter(
                    (slot) => slot.totalSlots - slot.bookedSlots > 0
                );

                if (!response.slotsAvailable || !slotsAvailable.length) {
                    setIsLoading(false);
                    setAvailableSlots([]);
                    showError
                        ? setSlotsErrMessage("No slots available, please try a different center")
                        : setSlotsErrMessage("");
                    return false;
                }

                const slotsArr = [];
                slotsAvailable.map((slot) => {
                    if (isFastingRequired) {
                        if (new Date(slot.slotEndTime).getUTCHours() <= 12) {
                            slotsArr.push(
                                `${utils.formatTime(slot.slotStartTime)} - ${utils.formatTime(
                                    slot.slotEndTime
                                )}`
                            );
                        }
                    } else {
                        slotsArr.push(
                            `${utils.formatTime(slot.slotStartTime)} - ${utils.formatTime(
                                slot.slotEndTime
                            )}`
                        );
                    }
                    return slot;
                });

                setAvailableSlots(slotsArr);
                setSlotsErrMessage("");
            } else {
                setAvailableSlots(utils.getDefaultSlots());
            }

            setIsLoading(false);
            return true;
        } catch (error) {
            showError
                ? setSlotsErrMessage("No slots available, please try a different center")
                : setSlotsErrMessage("");
            setIsLoading(false);
            return false;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        event.persist();
        setIsLoading(true);

        const actor = (props.utmParams.utm_actor || "unknown").toLowerCase();
        const mode = (props.utmParams.utm_mode || "unknown").toLowerCase();
        let trigger = (props.utmParams.utm_trigger || "unknown").toLowerCase();

        if (trigger.includes("n8n")) trigger = "n8n";
        else if (trigger.includes("salesportal")) trigger = "salesportal";
        else if (trigger.includes("mbinurancepusher")) trigger = "mbinsurancepusher";
        else trigger = trigger.toLowerCase();

        let source = `${actor}_sal_${mode}_${trigger}`;

        try {
            const payload = {
                pincode: formData.pincode,
                preferreddate1: utils.formatDateTime(
                    formData.preferredDate,
                    formData.preferredTime
                ),
                addressLine1: formData.address,
                landmark: formData.landmark,
                state: pincodeDetails.stateId,
                city: pincodeDetails.cityId,
                requestId: id,
                eQuote: wellnessId,
                policyBuyerName: policyBuyerName,
                insuranceCompanyName: insuranceCompanyName,
                campaignType: "GENERIC_PROD",
                providerId: formData.diagnosticCenter?.entity?.id,
                providerLocation: formData.diagnosticCenter?.entity?.providerLocation,
                source: source,
            };

            if (formData.visitType === ConstUtils.visitTypeEnum.HomeVisit) {
                payload.visitType = "Home Visit (Let professionals come at your home and collect samples)";
            } else {
                payload.dcName = formData.diagnosticCenter?.entity?.name;
                payload.diagnostics = formData.diagnosticCenter?.entity?.id;
            }

            DataSource.postActivity({
                RequestId: id,
                CompaignId: "",
                TemplateName: "",
                EQuote: wellnessId,
                CallBackStatus: "",
                EventType: "Create_appointment_clicked",
                EventPayload: JSON.stringify(payload),
                meta: JSON.stringify(props.utmParams),
                campaignId: "202010201700",
            });

            await createAppointment(payload);
        } catch (err) {
            setBookingError(
                err?.response?.data?.message ||
                err?.message ||
                "Oops something went wrong while creating appointment"
            );
        }
    };

    const createAppointment = async (payload) => {
        try {
            const response = await DataSource.createAppointment(payload);
            setIsLoading(false);

            if (response.data.success) {
                history.push("/appointment", {
                    address: response.data?.appointmentId
                        ? response.data?.caseDetails?.appointmentAddress
                        : formData.visitType === ConstUtils.visitTypeEnum.DcVisist
                            ? `${formData.diagnosticCenter?.entity?.name}, ${formData.diagnosticCenter?.entity?.address}`
                            : `${formData.address}`,
                    requestId: response.data?.appointmentId || props.caseDetails?.wellnessId,
                    caseDetails: props.caseDetails,
                    slotDetails: {
                        appointmentDate: utils.getCurrentDate(new Date(formData.preferredDate)),
                        appointmentTime: formData.preferredTime.split("-")[0]?.trim(),
                    },
                    showAddress: true,
                    landedFromAppointmentPage: true,
                });

                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            } else {
                setBookingError(
                    response.data?.message ||
                    "Error occured while creating appointment please try again"
                );
            }
        } catch (err) {
            setBookingError(
                err?.response?.data?.message ||
                err?.message ||
                "Oops something went wrong while creating appointment"
            );
        }
    };

    return (
        <div className="form-container">
            {
                isLoading &&
                <div className="custom-loader">
                    <DefaultLoader />
                </div>
            }

            <span className="form-title">
                Schedule your pre-policy medical checkup
            </span>

            {window.innerWidth < 576 && (
                <PolicyDetails caseData={props.caseDetails} />
            )}

            <Form noValidate onSubmit={handleSubmit}>
                <Row className="mt-3">
                    <Col xs={12} sm={4} md={4}>
                        <Form.Group controlId="formPinCode">
                            <Form.Label className="appointment-form-label">
                                Pin code<span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="Enter Pin Code"
                                value={formData.pincode}
                                onChange={(e) => handleInputChange("pincode", e.target.value)}
                                isInvalid={!isValidPincode}
                                style={{ color: "#70829B" }}
                                className="form-inputs"
                            />
                            <Form.Control.Feedback type="invalid">
                                {pincodeErrMessage}
                            </Form.Control.Feedback>
                            {!pincodeErrMessage && (
                                <Form.Text style={{ color: "#DA841E", fontWeight: "500" }}>
                                    Please verify your Pin code before proceeding
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                    <Col xs={6} sm={4} md={4}>
                        <Form.Group controlId="formCity">
                            <Form.Label className="appointment-form-label">City</Form.Label>
                            <Form.Control
                                type="text"
                                value={pincodeDetails.cityName}
                                disabled={true}
                                style={{ color: "#70829B" }}
                                className="form-inputs"
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={6} sm={4} md={4}>
                        <Form.Group controlId="formState">
                            <Form.Label className="appointment-form-label">State</Form.Label>
                            <Form.Control
                                type="text"
                                value={pincodeDetails.stateName}
                                disabled={true}
                                style={{ color: "#70829B" }}
                                className="form-inputs"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {isPackageMapped && (
                    <Form.Group>
                        <Form.Label className="appointment-form-label">
                            Visit Type<span className="text-danger">*</span>
                        </Form.Label>
                        <div className="visit-type-options">
                            <label
                                className={`visit-type ${formData.visitType === ConstUtils.visitTypeEnum.HomeVisit
                                    ? "selected"
                                    : ""
                                    }`}
                            >
                                <div className="visit-content">
                                    <div
                                        style={{ display: "flex", justifyContent: "space-between" }}
                                    >
                                        <span className="icon">
                                            <img
                                                src={`${ConstUtils.StaticS3ImageUrl}/HomeVisit.svg`}
                                                alt=""
                                            />
                                            {ConstUtils.visitTypeEnum.HomeVisit}
                                        </span>
                                        <input
                                            type="radio"
                                            name="visitType"
                                            value={ConstUtils.visitTypeEnum.HomeVisit}
                                            checked={
                                                formData.visitType ===
                                                ConstUtils.visitTypeEnum.HomeVisit
                                            }
                                            onChange={() =>
                                                handleInputChange(
                                                    "visitType",
                                                    ConstUtils.visitTypeEnum.HomeVisit
                                                )
                                            }
                                            disabled={!isHomeVisitMapped}
                                            className="form-inputs"
                                        />
                                    </div>
                                    <div>
                                        <small className="visit-type-label">
                                            Process takes up to <br className="hide-line-break" /> 30
                                            mins
                                        </small>
                                    </div>
                                </div>
                            </label>
                            {
                                !isHomeVisitMapped &&
                                <label
                                    className={`visit-type ${formData.visitType === ConstUtils.visitTypeEnum.DcVisist
                                        ? "selected"
                                        : ""
                                        }`}
                                >
                                    <div className="visit-content">
                                        <div
                                            style={{ display: "flex", justifyContent: "space-between" }}
                                        >
                                            <span className="icon">
                                                <img
                                                    src={`${ConstUtils.StaticS3ImageUrl}/CenterVisit.svg`}
                                                    alt=""
                                                />
                                                Center Visit
                                            </span>
                                            <input
                                                type="radio"
                                                name="visitType"
                                                value={ConstUtils.visitTypeEnum.DcVisist}
                                                checked={
                                                    formData.visitType === ConstUtils.visitTypeEnum.DcVisist
                                                }
                                                onChange={() =>
                                                    handleInputChange(
                                                        "visitType",
                                                        ConstUtils.visitTypeEnum.DcVisist
                                                    )
                                                }
                                                className="form-inputs"
                                            />
                                        </div>
                                        <div>
                                            <small className="visit-type-label">
                                                Lab visit can take up to{" "}
                                                <br className="hide-line-break" /> 2 hrs
                                            </small>
                                        </div>
                                    </div>
                                </label>
                            }
                        </div>
                        {visitTypeError && (
                            <div className="text-danger error-message">{visitTypeError}</div>
                        )}
                    </Form.Group>
                )}

                <Row>
                    <Col xs={12} sm={6}>
                        <Form.Group controlId="preferredDate" className="">
                            <Form.Label className="appointment-form-label">
                                Preferred Date<span className="text-danger">*</span>
                            </Form.Label>
                            <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                <DatePicker
                                    selected={formData.preferredDate}
                                    onChange={handleDateChange}
                                    minDate={utils.getMinDate()}
                                    maxDate={utils.getMaxDate()}
                                    dateFormat="dd-MMM-yyyy"
                                    placeholderText="DD-MM-YYYY"
                                    className="form-inputs form-control"
                                    popperClassName="custom-date-picker"
                                    calendarClassName="calendar-container"
                                    showPopperArrow={false}
                                />
                                <span className="dropdown-icon">
                                    <img
                                        src={`${ConstUtils.StaticS3ImageUrl}/Calendar_Grey.svg`}
                                        alt=""
                                    />
                                </span>
                            </div>
                            {dateErrMessage && (
                                <div className="text-danger error-message">
                                    {dateErrMessage}
                                </div>
                            )}
                        </Form.Group>
                    </Col>

                    {(!isPackageMapped ||
                        formData.visitType === ConstUtils.visitTypeEnum.HomeVisit) && (
                            <>
                                <Col xs={12} sm={6}>
                                    <Form.Group controlId="preferredTime">
                                        <Form.Label className="appointment-form-label">
                                            Preferred Slot<span className="text-danger">*</span>
                                        </Form.Label>
                                        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                            <Form.Control
                                                type="text"
                                                placeholder="HH:MM"
                                                value={formData.preferredTime}
                                                onChange={handleSlotChange}
                                                className="form-inputs"
                                                onFocus={() => setSlotDropDownVisible(true)}
                                                onBlur={() =>
                                                    setTimeout(() => setSlotDropDownVisible(false), 200)
                                                }
                                                inputMode="none"
                                            />
                                            <span className="dropdown-icon">
                                                <img
                                                    src={`${ConstUtils.StaticS3ImageUrl}/DownArrorGrey.svg`}
                                                    alt=""
                                                />
                                            </span>
                                        </div>
                                        {slotDropDownVisible && (
                                            <ListGroup
                                                className="position-absolute dropdown-input"
                                                style={{ width: "93%" }}
                                            >
                                                {availableSlots.length > 0 ? (
                                                    availableSlots.map((time, index) => (
                                                        <ListGroup.Item
                                                            key={index}
                                                            action
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                selectSlot(time, index)
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                            className="border-bottom-0"
                                                        >
                                                            <span> {time} </span>
                                                        </ListGroup.Item>
                                                    ))
                                                ) : (
                                                    <ListGroup.Item>No results found</ListGroup.Item>
                                                )}
                                            </ListGroup>
                                        )}
                                    </Form.Group>
                                </Col>
                                {slotsErrMessage && (
                                    <Col xs={12} sm={6}>
                                        <div className="text-danger error-message">
                                            {slotsErrMessage}
                                        </div>
                                    </Col>
                                )}
                            </>
                        )}
                </Row>

                {isPackageMapped &&
                    formData.visitType === ConstUtils.visitTypeEnum.DcVisist && (
                        <Form.Group className="position-relative">
                            <Form.Label className="appointment-form-label">
                                Diagnostic Center<span className="text-danger">*</span>
                            </Form.Label>
                            <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search Diagnostic Center"
                                    value={dcSearchValue}
                                    onChange={handleDCSearchChange}
                                    onFocus={() => setDcDropDownVisible(true)}
                                    onBlur={() =>
                                        setTimeout(() => {
                                            setDcDropDownVisible(false);
                                            if (filteredDiagnosticCenters.length === 0) {
                                                setDCSearchValue("");
                                            }
                                        }, 200)
                                    }
                                    className="form-inputs"
                                    style={{ paddingRight: "30px" }}
                                />
                                <span className="dropdown-icon">
                                    <img
                                        src={`${ConstUtils.StaticS3ImageUrl}/DownArrorGrey.svg`}
                                        alt=""
                                    />
                                </span>
                            </div>

                            {dcDropdownVisible && (
                                <ListGroup className="position-absolute w-100 dropdown-input">
                                    {filteredDiagnosticCenters.length > 0 ? (
                                        filteredDiagnosticCenters.map((center, index) => (
                                            <ListGroup.Item
                                                key={index}
                                                action
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    selectDiagnosticCenter(center, index);
                                                }}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                                className="border-bottom-0"
                                            >
                                                <span className="dc-name">{center?.entity?.name}</span>
                                                <br />
                                                <span className="dc-details">
                                                    {center?.entity?.distance +
                                                        ", " +
                                                        center?.entity?.address}
                                                </span>
                                            </ListGroup.Item>
                                        ))
                                    ) : (
                                        <ListGroup.Item>No results found</ListGroup.Item>
                                    )}
                                </ListGroup>
                            )}
                            {dcSearchError && (
                                <div className="text-danger error-message">{dcSearchError}</div>
                            )}
                        </Form.Group>
                    )}

                {isPackageMapped &&
                    formData.visitType === ConstUtils.visitTypeEnum.DcVisist && (
                        <Row>
                            <Col xs={12} sm={6}>
                                <Form.Group controlId="preferredTime">
                                    <Form.Label className="appointment-form-label">
                                        Preferred Slot<span className="text-danger">*</span>
                                    </Form.Label>
                                    <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                        <Form.Control
                                            type="text"
                                            placeholder="HH:MM"
                                            value={formData.preferredTime}
                                            onChange={handleSlotChange}
                                            className="form-inputs"
                                            onFocus={() => setSlotDropDownVisible(true)}
                                            onBlur={() =>
                                                setTimeout(() => setSlotDropDownVisible(false), 200)
                                            }
                                            inputMode="none"
                                        />
                                        <span className="dropdown-icon">
                                            <img
                                                src={`${ConstUtils.StaticS3ImageUrl}/DownArrorGrey.svg`}
                                                alt=""
                                            />
                                        </span>
                                    </div>
                                    {slotDropDownVisible && (
                                        <ListGroup
                                            className="position-absolute dropdown-input"
                                            style={{ width: "93%" }}
                                        >
                                            {availableSlots.length > 0 ? (
                                                availableSlots.map((time, index) => (
                                                    <ListGroup.Item
                                                        key={index}
                                                        action
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            selectSlot(time, index)
                                                        }}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                        className="border-bottom-0"
                                                    >
                                                        <span> {time} </span>
                                                    </ListGroup.Item>
                                                ))
                                            ) : (
                                                <ListGroup.Item>No results found</ListGroup.Item>
                                            )}
                                        </ListGroup>
                                    )}
                                </Form.Group>
                            </Col>
                            {slotsErrMessage && (
                                <Col xs={12} sm={12}>
                                    <div className="text-danger error-message">
                                        {slotsErrMessage}
                                    </div>
                                </Col>
                            )}
                        </Row>
                    )}

                {(formData.visitType === ConstUtils.visitTypeEnum.HomeVisit ||
                    !isPackageMapped) && (
                        <Form.Group controlId="exampleForm.ControlTextarea1">
                            <Form.Label className="appointment-form-label">
                                Address<span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Enter your address..."
                                value={formData.address}
                                onChange={(e) => handleInputChange("address", e.target.value)}
                                disabled={!(formData.pincode && isValidPincode)}
                                style={{ color: "#70829B" }}
                                className="form-inputs"
                            />
                        </Form.Group>
                    )}

                {(formData.visitType === ConstUtils.visitTypeEnum.HomeVisit ||
                    !isPackageMapped) && (
                        <Form.Group controlId="exampleForm.ControlInput1">
                            <Form.Label className="appointment-form-label">Landmark</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter nearby landmark"
                                value={formData.landmark}
                                onChange={(e) => handleInputChange("landmark", e.target.value)}
                                disabled={!(formData.pincode && isValidPincode)}
                                style={{ color: "#70829B" }}
                                className="form-inputs"
                            />
                        </Form.Group>
                    )}

                <div>
                    <Button
                        type="submit"
                        disabled={!isFormValid()}
                        className="appointment-button"
                    >
                        Request Appointment
                    </Button>
                </div>

                {bookingError && (
                    <div className="text-danger mt-2 error-message">{bookingError}</div>
                )}

                {
                    window.innerWidth <= 576 && (
                        <div style={{ textAlign: "center", fontSize: "14px", padding: "20px", marginTop: "8px" }}>
                            <p className="footer-help-contact mb-0">
                                Call {""}
                                <a href="tel:08068239362" className="footer-help-link">
                                    080-68239362
                                </a>
                                , 10am to 6pm for any queries
                            </p>
                        </div>
                    )
                }
            </Form>
        </div>
    );
};

export default AppointmentForm;
